<template>
  <fts-modal
    append-to-body
    width="480px"
    :header="false"
    class="fts-canvas-guard-dialog"
    @close="cancel"
    v-if="showDialog"
  >
    <template #body>
      <div class="guard-title">
        {{ mode === 'revert' ? 'Revert this change?' : `Delete ${unitLabel}?` }}
        <fts-release-badge alpha class="fts-align-top" />
      </div>

      <div class="guard-description" v-if="mode !== 'revert'">
        Deleting
        <span class="guard-subject">{{ subject }}</span>
        also affects the configuration below. Only what you keep checked is changed with it;
        unchecking anything means the delete cannot proceed cleanly.
      </div>
      <div class="guard-description" v-else>
        Restoring
        <span class="guard-subject">{{ subject }}</span>.
        Rows marked as conflicts were edited after this change was applied — restoring them
        overwrites that later work, so they start unchecked.
      </div>

      <div class="guard-rows">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="guard-row"
          :class="'guard-row-' + row.kind"
        >
          <fts-checkbox
            v-if="row.checkable"
            v-model="row.checked"
            class="guard-row-check"
          />
          <font-awesome-icon
            v-else
            :icon="['fas', 'info-circle']"
            class="guard-row-info"
          />
          <div class="guard-row-body">
            <div class="guard-row-label">{{ row.label }}</div>
            <div class="guard-row-reason" v-if="row.reason">{{ row.reason }}</div>
            <div class="guard-row-tag" v-if="row.kind === 'formula'">clears a formula</div>
            <div class="guard-row-tag" v-if="row.kind === 'auth'">permission rule</div>
            <div class="guard-row-tag guard-row-tag-conflict" v-if="row.conflict">conflict</div>
          </div>
        </div>
        <div class="guard-empty" v-if="!rows.length">
          Nothing else references this — it can be {{ mode === 'revert' ? 'restored' : 'deleted' }} cleanly.
        </div>
      </div>
    </template>

    <template #footer>
      <div
        class="guard-blocked-hint"
        v-if="mode !== 'revert' && !allRequiredChecked"
      >
        Everything listed must go with it — with rows unchecked the delete cannot proceed
        cleanly. Use <b>Delete anyway</b> to force it (referrers stay orphaned).
      </div>
      <div class="guard-footer">
        <fts-btn text text-transform-init @click="cancel">Cancel</fts-btn>

        <fts-btn
          v-if="mode !== 'revert'"
          text
          color="danger"
          text-transform-init
          @click="deleteAnyway"
        >
          Delete anyway
        </fts-btn>

        <fts-btn
          color="danger"
          box-shadow-none
          text-transform-init
          :disabled="mode !== 'revert' && !allRequiredChecked"
          @click="confirm"
        >
          {{ confirmLabel }}
        </fts-btn>
      </div>
    </template>
  </fts-modal>
</template>

<script>
/* The D11 canvas guard dialog — the referrer checklist for guarded deletes, doubling as
   the D12 revert checklist. Promise-based like FTSInformationDeleteConfirmation:
   show(...) resolves — once the dialog has closed — with
     delete mode: { mode: 'cascade', ops, authRows } | { mode: 'legacy' } | false
     revert mode: { ops } | false
   The "Delete anyway" escape runs the legacy delete path — the guard informs humans and
   binds the AI (D11); the escape is the designer's authority, not a gap. */

import FTSModal from "../elements/FTSModal";
import FTSBtn from "../elements/FTSBtn";
import FTSCheckbox from "../elements/FTSCheckbox";
import FTSReleaseBadge from "../elements/FTSReleaseBadge";

export default {
  components: {
    "fts-modal": FTSModal,
    "fts-btn": FTSBtn,
    "fts-checkbox": FTSCheckbox,
    "fts-release-badge": FTSReleaseBadge,
  },
  data: () => ({
    showDialog: false,
    resolve: null,
    mode: "delete",
    unitLabel: "",
    subject: "",
    rows: [],
    target: null,
  }),
  computed: {
    allRequiredChecked() {
      return this.rows.every((row) => !row.checkable || row.checked);
    },
    checkedRows() {
      return this.rows.filter((row) => row.checkable && row.checked);
    },
    confirmLabel() {
      if (this.mode === "revert") {
        const n = this.checkedRows.length;
        return n === this.rows.length ? "Restore" : `Restore ${n} selected`;
      }
      return "Delete";
    },
  },
  methods: {
    /* Delete mode: composed = composeCascade() result. Revert mode: revert =
       buildRevertProposal() result plus the record's authRows note. */
    show({ mode, unitLabel, subject, composed, revert }) {
      this.mode = mode || "delete";
      this.unitLabel = unitLabel || "";
      this.subject = subject || "";
      if (this.mode === "revert") {
        this.rows = revert.rows.map((row) => ({
          kind: "blocking",
          label: row.label,
          reason: row.reason,
          conflict: row.conflict,
          op: row.op,
          checkable: true,
          checked: !row.conflict,
        }));
        this.target = null;
      } else {
        this.rows = composed.rows.map((row) => ({
          kind: row.kind,
          label: row.label,
          op: row.op,
          authRow: row.authRow,
          checkable: row.checkable,
          checked: row.defaultChecked,
        }));
        this.target = composed.target;
      }
      return new Promise((resolve) => {
        this.resolve = resolve;
        this.showDialog = true;
      });
    },
    close() {
      this.showDialog = false;
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    /* Close first; answer only after the close has rendered (SS06174). The caller applies the
       delete as soon as it has the answer, and that can unmount this dialog's host:
       FTSConsoleElement is rendered only while its card has items, so deleting the last listing
       removes it. Answered in the same tick, that unmount ran in the flush meant to close the
       dialog, destroying the modal (already moved to <body> by append-to-body) in place — it
       stayed on screen with every button dead. */
    settle(answer) {
      const resolve = this.resolve;
      this.close();
      this.$nextTick(() => resolve && resolve(answer));
    },
    cancel() {
      this.settle(false);
    },
    deleteAnyway() {
      this.settle({ mode: "legacy" });
    },
    confirm() {
      if (this.mode === "revert") {
        this.settle({
          ops: this.checkedRows.filter((row) => row.op).map((row) => row.op),
        });
      } else {
        this.settle({
          mode: "cascade",
          ops: this.checkedRows.filter((row) => row.op).map((row) => row.op)
            .concat(this.target ? [this.target] : []),
          authRows: this.checkedRows.filter((row) => row.authRow).map((row) => row.authRow),
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars" as *;

.fts-canvas-guard-dialog {
  .guard-title {
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5;
  }
  .guard-description {
    font-size: 0.875rem;
    line-height: 1.5;
    margin-top: 0.5rem;
  }
  .guard-subject {
    font-weight: 500;
    word-break: break-word;
  }
  .guard-rows {
    margin-top: 1rem;
    max-height: 320px;
    overflow-y: auto;
  }
  .guard-row {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.375rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    line-height: 1.5;
    &:not(:first-child) {
      margin-top: 0.25rem;
    }
    &.guard-row-formula {
      background-color: var(--fts-orange-050, #fff7ed);
    }
    &.guard-row-auth {
      background-color: var(--fts-red-050, #fef2f2);
    }
    &.guard-row-notice {
      color: var(--fts-gray-600);
      background-color: var(--fts-gray-100);
    }
  }
  .guard-row-check {
    margin-top: 0.125rem;
  }
  .guard-row-info {
    margin-top: 0.3125rem;
    color: var(--fts-gray-500);
    font-size: 0.75rem;
  }
  .guard-row-body {
    flex: 1;
    word-break: break-word;
  }
  .guard-row-reason {
    font-size: 0.75rem;
    color: var(--fts-gray-600);
  }
  .guard-row-tag {
    display: inline-block;
    font-size: 0.6875rem;
    padding: 0 0.375rem;
    border-radius: 0.625rem;
    background-color: var(--fts-gray-200);
    color: var(--fts-gray-700);
    margin-top: 0.125rem;
    &.guard-row-tag-conflict {
      background-color: var(--fts-red-100, #fee2e2);
      color: var(--fts-red-700, #b91c1c);
    }
  }
  .guard-empty {
    font-size: 0.875rem;
    color: var(--fts-gray-600);
    padding: 0.375rem 0.5rem;
  }
  .guard-blocked-hint {
    font-size: 0.75rem;
    line-height: 1.5;
    color: var(--fts-gray-600);
    margin-bottom: 0.5rem;
    text-align: right;
  }
  .guard-footer {
    @include flex-gap($gap: 0.5rem);
    justify-content: flex-end;
  }
}
</style>
